import * as op from '../../common/errors/OperationResult'
import type { ObjectTypesService } from '@/object-types/services/ObjectTypesService.ts'
import { BehaviorSubject, combineLatest, filter, map, take, type Observable } from 'rxjs'
import type { AppLifecycleService } from '@/core/lifecycle/AppLifecycleService.ts'
import type { IBehaviorObservable } from '@/common/react/IBehaviorObservable.ts'
import type { CollectionViewsApiService } from '@/objects/services/CollectionViewsApiService.ts'
import type {
  CollectionViewColumnModel,
  CollectionViewSettingsModel,
  FilterNodeInput,
  UpdateCollectionTabInput,
  UpdateCollectionViewColumnInput as _UpdateCollectionViewColumnInput,
  UpdateCollectionViewSettingsInput,
} from '@/generated/graphql.ts'
import { FilterLogicalOperator } from '@/generated/graphql.ts'
import { localStorageKeys } from '@/core/configs/local-storage-keys.ts'
import { ObjectLocalStorageValue } from '@/common/local-storage/ObjectLocalStorageValue.ts'
import type { CollectionTabModel, ObjectTypeModel, UpdateCollectionViewColumnInput } from '@/core/gql/gql-models-override.ts'
import { createTmpClientId, isClientId } from '@/common/network/gql-types.ts'
import { assertLog, logError } from '@/common/errors/log-utils.ts'
import type { FilterNodeModel } from '@/core/gql/filter-models.ts'
import type { OperationResult } from '@/common/errors/OperationResult.ts'

export interface ICollectionViewLocalStorageState {
  lastOpenedCollectionId: string
  lastOpenedCollectionTypeId: number
  // lastOpenedCollectionTabId: number
}

export class CollectionViewsService {
  public get localStorageState(): ICollectionViewLocalStorageState | undefined {
    if (!this._localStorageState.wasRead) {
      this._localStorageState.read()
    }
    return this._localStorageState.value
  }

  public set localStorageState(value: ICollectionViewLocalStorageState) {
    const state = this.localStorageState
    if (
      state &&
      state.lastOpenedCollectionTypeId == value.lastOpenedCollectionTypeId &&
      state.lastOpenedCollectionId == value.lastOpenedCollectionId
    ) {
      return
    }

    this._localStorageState.value = value
  }

  private _localStorageState = new ObjectLocalStorageValue<ICollectionViewLocalStorageState>(
    localStorageKeys.LastOpenedCollection,
  )
  private _collectionIdToViewSettings = new Map<
    string,
    BehaviorSubject<CollectionViewSettingsModel | undefined>
  >()
  private _collectionIdToTabs = new Map<string, BehaviorSubject<CollectionTabModel[] | undefined>>()
  private _collectionIdToActiveTab = new Map<string, BehaviorSubject<number | undefined>>()
  private _inFlightFetches = new Set<string>()

  constructor(
    private readonly typesService: ObjectTypesService,
    private readonly apiService: CollectionViewsApiService,
    private readonly lifecycleService: AppLifecycleService,
  ) {
    this.init()
    // lifecycleService.onAfterFirstRender$.subscribe(this.onAfterFirstRender.bind(this))
  }

  public getActiveTabId$(collectionId: string): IBehaviorObservable<number | undefined> {
    return this.getActiveTabIdSubject(collectionId)
  }

  public getActiveTab$(collectionId: string): Observable<CollectionTabModel | undefined> {
    return combineLatest([
      this.getTabsSubject(collectionId),
      this.getActiveTabIdSubject(collectionId),
    ]).pipe(map(([tabs, id]) => tabs?.find((t) => t.id === id)))
  }

  public getActiveTab(collectionId: string): CollectionTabModel | undefined {
    const tabs = this.getTabsSubject(collectionId).value
    const id = this.getActiveTabIdSubject(collectionId).value
    return tabs?.find((t) => t.id === id)
  }

  public setActiveTabId(collectionId: string, tabId: number): void {
    this.getActiveTabIdSubject(collectionId).next(tabId)
    localStorage.setItem(`${localStorageKeys.CollectionTabPrefix}${collectionId}`, String(tabId))
  }

  public getViewSettings$(
    colId: string,
  ): IBehaviorObservable<CollectionViewSettingsModel | undefined> {
    return this.getViewSettingsSubject(colId)
  }

  public getTabs$(colId: string): IBehaviorObservable<CollectionTabModel[] | undefined> {
    return this.getTabsSubject(colId)
  }

  public updateViewSettings(
    collectionId: string,
    update: Omit<UpdateCollectionViewSettingsInput, 'id' | 'columns'>,
  ) {
    const subj = this.getViewSettingsSubject(collectionId)
    const current = subj.value
    if (!current) return

    subj.next({
      ...current,
      ...(update.groupByColumnId !== undefined && { groupByColumnId: update.groupByColumnId }),
    })

    void this.apiService.updateViewSettings({ id: current.id, ...update })
  }

  public updateColumn(
    collectionId: string,
    propertyDefinitionId: number,
    update: Partial<Omit<CollectionViewColumnModel, '__typename' | 'propertyDefinitionId'>>,
  ) {
    this.updateColumns(collectionId, [{ propertyDefinitionId, ...update }])
  }

  public updateColumns(collectionId: string, updates: Array<UpdateCollectionViewColumnInput>) {
    const subj = this.getViewSettingsSubject(collectionId)
    const current = subj.value
    if (!current) {
      logError('updateColumns - no view settings for collectionId')
      return
    }
    if (updates.length === 0) return

    const columns = current.columns.map((col) => {
      const u = updates.find((x) => x.propertyDefinitionId === col.propertyDefinitionId)
      if (!u) return col
      const updated = { ...col }
      if (u.order != null) updated.order = u.order
      if (u.widthWeight != null) updated.widthWeight = u.widthWeight
      if (u.isVisible != null) updated.isVisible = u.isVisible
      return updated
    })

    subj.next({ ...current, columns: columns.sort((a, b) => a.order - b.order) })

    const persistedUpdates = updates.filter((u) => !isClientId(u.propertyDefinitionId))
    if (persistedUpdates.length === 0) return

    const input: UpdateCollectionViewSettingsInput = {
      id: current.id,
      columns: persistedUpdates as _UpdateCollectionViewColumnInput[],
    }
    void this.apiService.updateViewSettings(input)
  }

  public updateActiveTab(collectionId: string, input: Omit<UpdateCollectionTabInput, 'id'>) {
    this.updateTab(collectionId, this.getActiveTabIdSubject(collectionId).value!, input)
  }

  public async createTab(collectionId: string, name: string): Promise<OperationResult> {
    const tabs = this.getTabsSubject(collectionId)
    if (!tabs.value) {
      logError('createTab - no tabs for collectionId')
      return op.error('no tabs')
    }
    const tmpId = createTmpClientId()
    const optimistic: CollectionTabModel = {
      id: tmpId,
      collectionId,
      name,
      order: tabs.value.length,
      filterTree: [],
    }
    tabs.next([...tabs.value, optimistic])
    this.getActiveTabIdSubject(collectionId).next(tmpId)

    const { result, error } = await this.apiService.createCollectionTab({ collectionId, name })
    if (error) {
      tabs.next(tabs.value.filter((t) => t.id !== tmpId))
      return op.error(error)
    }
    const current = tabs.value
    const idx = current.findIndex((t) => t.id === tmpId)
    if (idx !== -1) {
      const next = [...current]
      next[idx] = result!
      tabs.next(next)
    }
    const activeTab = this.getActiveTabIdSubject(collectionId)
    if (activeTab.value === tmpId) {
      this.setActiveTabId(collectionId, result!.id)
    }
    return op.success()
  }

  public async deleteTab(collectionId: string, tabId: number): Promise<OperationResult> {
    const tabs = this.getTabsSubject(collectionId)
    if (!tabs.value) {
      logError('deleteTab - no tabs for collectionId')
      return op.error('no tabs')
    }
    const prev = tabs.value
    const next = prev.filter((t) => t.id !== tabId)
    tabs.next(next)

    const activeTab = this.getActiveTabIdSubject(collectionId)
    if (activeTab.value === tabId && next.length > 0) {
      this.setActiveTabId(collectionId, next[0].id)
    }

    if (isClientId(tabId)) {
      return op.success()
    }

    const { error } = await this.apiService.deleteCollectionTab({ id: tabId })
    if (error) {
      tabs.next(prev)
      return op.error(error)
    }
    return op.success()
  }

  public updateTab(
    collectionId: string,
    tabId: number,
    input: Omit<UpdateCollectionTabInput, 'id'>,
  ): void {
    const tabs = this.getTabsSubject(collectionId)
    if (!tabs.value) {
      logError('updateTab - no tabs for collectionId')
      return
    }
    const tabIndex = tabs.value.findIndex((x) => x.id == tabId)
    if (tabIndex === -1) {
      logError(`updateTab - no tab with id: ${tabId}`)
    }
    const newTabs = [...tabs.value]
    newTabs[tabIndex] = this.updateTabImmutable(tabs.value[tabIndex], input)
    tabs.next(newTabs)

    if (isClientId(tabId)) {
      logError('trying to update local-id tab: ', { tabId })
      return
    }

    void this.apiService.updateCollectionTab({ id: tabId, ...input })
  }

  public fetchIfNotFetched(collectionId: string) {
    if (this.getViewSettings$(collectionId).value) return
    if (this._inFlightFetches.has(collectionId)) return

    void this.fetch(collectionId)
  }

  private updateTabImmutable(
    tab: CollectionTabModel,
    input: Omit<UpdateCollectionTabInput, 'id'>,
  ): CollectionTabModel {
    return {
      id: tab.id,
      collectionId: tab.collectionId,
      name: input.name ?? tab.name,
      order: tab.order,
      filterTree:
        input.filterTree != null
          ? input.filterTree.map((x) => this.createFilterNodeFromInput(x))
          : tab.filterTree,
    }
  }

  private async fetch(collectionId: string): Promise<OperationResult> {
    assertLog(!this._inFlightFetches.has(collectionId))
    this._inFlightFetches.add(collectionId)

    const { result, error } = await this.apiService.getViewSettingsAndTabs(collectionId)
    if (error) {
      return op.error(error)
    }

    this.getViewSettingsSubject(collectionId).next(result!.collectionViewSettings)
    const tabs = this.getTabsSubject(collectionId)
    tabs.next(result!.collectionTabs)
    const activeTab = this.getActiveTabIdSubject(collectionId)
    if (!activeTab.value) {
      activeTab.next(tabs.value![0].id)
    }
    this._inFlightFetches.delete(collectionId)
    return op.success()
  }

  private onNonEmptyTypesFetched(types: ObjectTypeModel[]) {
    void this.fetchSavedCollectionViewSettings(types)
  }

  private async fetchSavedCollectionViewSettings(types: ObjectTypeModel[]): Promise<void> {
    const { error } = await this.tryFetchSavedCollectionViewSettings(types)
    if (error) {
      logError(
        `error fetching viewSettings for lastOpenedCollection. Clearing local storage state`,
        {
          error: error.message,
          state: this._localStorageState,
        },
      )
      this._localStorageState.value = undefined
    } else {
      console.log('success fetching viewSettings for lastOpenedCollection')
    }
  }

  private async tryFetchSavedCollectionViewSettings(
    types: ObjectTypeModel[],
  ): Promise<OperationResult> {
    const lastOpenedCollectionTypeId = this.localStorageState!.lastOpenedCollectionTypeId

    const t = types.find((x) => x.id === lastOpenedCollectionTypeId)
    if (!t) {
      return op.error(
        `no type among fetched type for ${JSON.stringify({ lastOpenedCollectionTypeId })}`,
      )
    }

    return await this.fetch(this.localStorageState!.lastOpenedCollectionId)
  }

  private init() {
    const lastOpenedCollectionTypeId = this.localStorageState?.lastOpenedCollectionTypeId
    if (!lastOpenedCollectionTypeId) {
      return
    }

    this.typesService.selectedSpaceTypes$
      .pipe(
        filter((x) => !!x),
        take(1),
      )
      .subscribe((types) => this.onNonEmptyTypesFetched(types))
  }

  private getViewSettingsSubject(
    colId: string,
  ): BehaviorSubject<CollectionViewSettingsModel | undefined> {
    let viewSettings = this._collectionIdToViewSettings.get(colId)
    if (!viewSettings) {
      viewSettings = new BehaviorSubject<CollectionViewSettingsModel | undefined>(undefined)
      this._collectionIdToViewSettings.set(colId, viewSettings)
    }
    return viewSettings
  }

  private getActiveTabIdSubject(collectionId: string): BehaviorSubject<number | undefined> {
    let subj = this._collectionIdToActiveTab.get(collectionId)
    if (!subj) {
      const stored = localStorage.getItem(`${localStorageKeys.CollectionTabPrefix}${collectionId}`)
      subj = new BehaviorSubject<number | undefined>(stored != null ? Number(stored) : undefined)
      this._collectionIdToActiveTab.set(collectionId, subj)
    }
    return subj
  }

  private getTabsSubject(colId: string): BehaviorSubject<CollectionTabModel[] | undefined> {
    let subj = this._collectionIdToTabs.get(colId)
    if (!subj) {
      subj = new BehaviorSubject<CollectionTabModel[] | undefined>(undefined)
      this._collectionIdToTabs.set(colId, subj)
    }
    return subj
  }

  private createFilterNodeFromInput(input: FilterNodeInput): FilterNodeModel {
    const id = Number(input.clientId)
    const __typename = 'FilterNodeModel'

    const parentId = input.parentClientId != null ? Number(input.parentClientId) : null
    return input.operator != null
      ? {
          __typename,
          id,
          parentId,
          logicalOperator: null,
          operator: input.operator,
          propertyDefinitionId: input.propertyDefinitionId!,
          value: input.value ?? null,
          values: input.values ? input.values.map(Number) : null,
        }
      : {
          __typename,
          id,
          parentId,
          logicalOperator: input.logicalOperator ?? FilterLogicalOperator.And,
          operator: null,
          propertyDefinitionId: null,
          value: null,
          values: null,
        }
  }
}
